import React from 'react';
import { DG } from '@cotera/sdk/core';
import { Icon } from '@cotera/client/app/components';

export type BreadcrumbItem = {
  url?: string;
  display: React.ReactNode;
};

/**
 * Creates breadcrumb navigation for run activity pages
 * @param runKey - The run key to parse
 * @param graph - The build explorer graph
 * @returns Array of breadcrumb items with display and optional URL
 */
export const makeBreadCrumbs = (
  runKey: string,
  graph: DG.BuildExplorer
): BreadcrumbItem[] => {
  const parts = runKey.split('|');
  const root = parts[0] ?? '';
  const rest = parts.slice(1);

  if (root === 'workflow-program-row' && rest.length === 0) {
    return [
      {
        display: <Icon icon="activity" />,
        url: `runs/${root}`,
      },
    ];
  }

  if (root === 'workflow-program-row' && rest.length > 0) {
    const relIdentity = DG.Graph.Identity.fromString(rest[0]!);
    const rel = graph.at(relIdentity).getBuiltRelByIdentity({ relIdentity });
    if (rel) {
      if (rest.length > 1) {
        const runKey = rest.slice(2).join('|');

        const colIdentity = DG.Graph.Identity.fromString(rest[1]!);
        const col = graph
          .at(colIdentity)
          .getBuiltColumnByIdentity({ relIdentity, colIdentity });

        if (col) {
          return [
            {
              display: <Icon icon="activity" />,
              url: `runs/${root}`,
            },
            {
              display: rel.symbol,
              url: `runs/${root}|${relIdentity.toString()}`,
            },
            {
              display: col.symbol,
              url: `runs/${root}|${relIdentity.toString()}|${colIdentity.toString()}`,
            },
            runKey.length > 0
              ? {
                  display: runKey,
                }
              : undefined,
          ].filter((item) => item !== undefined);
        }
      }
      return [
        {
          display: <Icon icon="activity" />,
          url: `runs/${root}`,
        },
        {
          display: rel.symbol,
          url: `runs/${root}|${relIdentity.toString()}`,
        },
      ];
    }
  }

  return [
    {
      display: root,
      url: `runs/${root}`,
    },
    ...rest.map((part, index) => ({
      display: part,
      url: `runs/${root}|${rest.slice(0, index + 1).join('|')}`,
    })),
  ];
};
